import { t } from '../i18n.js';
import { dismissThreshold } from './bottom-sheet.js';
import { exitAndRemove, reducedMotion } from './motion.js';
import { isDesktopViewport } from './breakpoint.js';
import { skipsInitialFocus, isActionRowDisabled } from './action-row.js';

const POPOVER_GAP = 8;
const POPOVER_EDGE_MARGIN = 8;

/**
 * Pure presentation decision — no DOM/layout access, so it is trivially
 * unit-testable without a real viewport. `matches` is the boolean result of
 * `matchMedia('(min-width: 1024px)').matches`.
 * @param {boolean} matches
 * @returns {'popover'|'sheet'}
 */
export function pickerPresentation(matches) {
  return matches ? 'popover' : 'sheet';
}


/**
 * Clamps an anchored popover's top-left position so it stays fully inside
 * the viewport. Pure — safe to call with jsdom's zeroed rects.
 * @param {{top:number,bottom:number,left:number,right:number}} triggerRect
 * @param {{width:number,height:number}} viewport
 * @param {{width:number,height:number}} panelSize
 * @returns {{top:number,left:number}}
 */
export function computePopoverPosition(triggerRect, viewport, panelSize) {
  let top = triggerRect.bottom + POPOVER_GAP;
  if (top + panelSize.height > viewport.height - POPOVER_EDGE_MARGIN) {
    top = Math.max(POPOVER_EDGE_MARGIN, triggerRect.top - panelSize.height - POPOVER_GAP);
  }
  top = Math.min(Math.max(top, POPOVER_EDGE_MARGIN), Math.max(POPOVER_EDGE_MARGIN, viewport.height - panelSize.height - POPOVER_EDGE_MARGIN));

  let left = triggerRect.left;
  left = Math.min(left, viewport.width - panelSize.width - POPOVER_EDGE_MARGIN);
  left = Math.max(left, POPOVER_EDGE_MARGIN);

  return { top, left };
}

function focusFirstFocusable(container) {
  const candidates = container.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  );
  for (const el of candidates) {
    // Destructive rows sort last already (partitionDestructive), so this only
    // changes anything when a row set is destructive-only — the case where
    // opening focus would otherwise land on the one row nobody asked to fire.
    if (!el.disabled && !isActionRowDisabled(el) && !skipsInitialFocus(el)) {
      el.focus();
      return;
    }
  }
  container.focus();
}

// The icon and ingredient pickers render ordinary tab order (no explicit
// tabindex on their buttons/inputs), but the ≥ lg overflow menu
// (rowActions.js) renders roving tabindex — one row at "0", the rest "-1".
// A selector built from tag names alone (`button, …`) matches every row
// regardless of its own tabindex, so `last` landed on a "-1" row whose
// boundary the browser's real Tab key never reaches: the check
// `activeElement === last` never matched, no `preventDefault()` ran, and Tab
// escaped the dialog. Filtering on the actual tabindex value treats the
// roving group as the single tab stop it is everywhere else, without
// touching plain buttons/inputs that never declared one.
function trapTab(panel) {
  panel.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const focusable = Array.from(
      panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]'),
    ).filter((el) => !el.disabled && el.getAttribute('tabindex') !== '-1');
    if (focusable.length === 0) {
      // Nothing to trap between — without this, Tab falls through to
      // whatever is next in the document, out of a dialog the user
      // believes is modal.
      e.preventDefault();
      panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (document.activeElement === panel) {
      // Opening focus fell through to the panel itself (skipsInitialFocus /
      // isActionRowDisabled left every candidate ineligible). The panel is
      // not itself in `focusable`, so neither boundary branch below can
      // ever match it — without this, Tab/Shift+Tab escapes to whatever is
      // tabbable just outside the dialog.
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
      return;
    }
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });
}

function resolveContent(content, api) {
  if (typeof content === 'function') {
    return content(api);
  }
  return content;
}

let currentClose = null;

function openAsBottomSheet({ trigger, title, content, onClose }) {
  if (currentClose) currentClose();

  const modalOpen = document.querySelector('[data-modal-open="true"]') !== null;
  const zClass = modalOpen ? 'z-modal-over' : 'z-50';

  const scrim = document.createElement('button');
  scrim.type = 'button';
  scrim.setAttribute('data-role', 'picker-sheet-scrim');
  scrim.setAttribute('aria-label', t('actions.close_sheet'));
  scrim.className = `fixed inset-0 bg-black/40 backdrop-blur-sm ${zClass} anim-fade-in`;
  // Mirror of the modal's `data-modal-open` probe, in the other direction:
  // modal.js reads this to keep its document-level Escape/Enter handlers
  // quiet while a sheet is stacked over it. Without it a single Escape
  // closes both, and the discard guard dismisses the form it is guarding.
  scrim.dataset.sheetOpen = 'true';

  const panel = document.createElement('div');
  panel.setAttribute('data-role', 'picker-sheet-panel');
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('tabindex', '-1');
  panel.className = [
    'fixed bottom-0 left-0 right-0 max-w-md mx-auto',
    'glass-panel',
    'rounded-t-2xl',
    'max-h-sheet-panel overflow-y-auto overscroll-contain',
    'px-4 pb-[max(1.375rem,env(safe-area-inset-bottom))]',
    'pt-2.5',
    zClass,
    'anim-sheet-up',
  ].join(' ');

  const handle = document.createElement('div');
  handle.setAttribute('data-role', 'picker-sheet-handle');
  handle.setAttribute('aria-hidden', 'true');
  handle.className = 'mx-auto mb-3 h-1 w-10 rounded-full bg-chip-neutral dark:bg-chip-neutral-dark';
  panel.append(handle);

  // rowActions.js requires `sheetTitle` on the grounds that it names the
  // dialog — but naming only happens if something on the panel points at
  // it. A bare <p> gave role="dialog" no accessible name at all.
  if (title) {
    const titleId = `picker-sheet-title-${Math.random().toString(36).slice(2, 9)}`;
    panel.setAttribute('aria-labelledby', titleId);
    const titleEl = document.createElement('p');
    titleEl.id = titleId;
    titleEl.className = 'text-xs font-semibold uppercase tracking-wide text-ink-muted mb-2';
    titleEl.textContent = title;
    panel.append(titleEl);
  }

  const body = document.createElement('div');
  body.setAttribute('data-role', 'picker-sheet-body');
  panel.append(body);

  function closeSheet() {
    if (currentClose !== closeSheet) return;
    currentClose = null;

    document.removeEventListener('keydown', onKeydown);
    if (dragActive) {
      dragActive = false;
      cancelDragListeners();
    }

    exitAndRemove(scrim, 'anim-fade-out');
    exitAndRemove(panel, 'anim-sheet-down');

    if (document.body.dataset.bodyLockOwner === 'picker-sheet') {
      document.body.classList.remove('overflow-hidden');
      delete document.body.dataset.bodyLockOwner;
    }

    if (trigger && typeof trigger.focus === 'function') {
      trigger.focus();
    }

    if (onClose) {
      try { onClose(); } catch {}
    }
  }

  body.append(resolveContent(content, { close: closeSheet }));

  if (!document.body.dataset.bodyLockOwner) {
    document.body.classList.add('overflow-hidden');
    document.body.dataset.bodyLockOwner = 'picker-sheet';
  }

  function onKeydown(e) {
    if (e.key === 'Escape') closeSheet();
  }
  document.addEventListener('keydown', onKeydown);
  scrim.addEventListener('click', closeSheet);
  trapTab(panel);

  // --- Drag-to-dismiss — mirrors bottom-sheet.js mechanics/thresholds ---
  let dragStartY = 0;
  let dragDelta = 0;
  let dragActive = false;

  function setChildrenPointerEvents(value) {
    for (const child of panel.children) {
      if (child !== handle) child.style.pointerEvents = value;
    }
  }

  function onDragMove(e) {
    if (!dragActive) return;
    dragDelta = Math.max(0, e.clientY - dragStartY);
    if (!reducedMotion()) {
      panel.style.transform = `translateY(${dragDelta}px)`;
    }
  }

  function cancelDragListeners() {
    document.removeEventListener('pointermove', onDragMove);
    document.removeEventListener('pointerup', onDragEnd);
    document.removeEventListener('pointercancel', onDragEnd);
  }

  function onDragEnd() {
    if (!dragActive) return;
    const delta = dragDelta;
    dragActive = false;
    cancelDragListeners();
    panel.style.touchAction = '';
    setChildrenPointerEvents('');

    if (delta >= dismissThreshold(panel.offsetHeight)) {
      closeSheet();
    } else if (!reducedMotion()) {
      panel.style.transition = 'transform 0.25s cubic-bezier(0.2,0.8,0.3,1)';
      panel.style.transform = 'translateY(0)';
      panel.addEventListener('transitionend', () => {
        panel.style.transition = '';
        panel.style.transform = '';
      }, { once: true });
    }
  }

  handle.addEventListener('pointerdown', (e) => {
    dragStartY = e.clientY;
    dragDelta = 0;
    dragActive = true;
    panel.classList.remove('anim-sheet-up');
    panel.style.touchAction = 'none';
    setChildrenPointerEvents('none');
    if (typeof handle.setPointerCapture === 'function') {
      try { handle.setPointerCapture(e.pointerId); } catch {}
    }
    document.addEventListener('pointermove', onDragMove);
    document.addEventListener('pointerup', onDragEnd);
    document.addEventListener('pointercancel', onDragEnd);
  });

  document.body.append(scrim);
  document.body.append(panel);
  focusFirstFocusable(panel);

  currentClose = closeSheet;
  return { close: closeSheet };
}

function openAsPopover({ trigger, title, content, onClose }) {
  if (currentClose) currentClose();

  const modalOpen = document.querySelector('[data-modal-open="true"]') !== null;
  const zClass = modalOpen ? 'z-modal-over' : 'z-50';

  const panel = document.createElement('div');
  panel.setAttribute('data-role', 'picker-popover-panel');
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('tabindex', '-1');
  panel.className = [
    'fixed',
    'glass-panel',
    'rounded-2xl',
    'p-3',
    'w-80 max-w-[90vw] max-h-[70vh]',
    'overflow-y-auto overscroll-contain',
    zClass,
    'anim-pop',
  ].join(' ');

  // See openAsBottomSheet — same accessible-name gap, same fix.
  if (title) {
    const titleId = `picker-popover-title-${Math.random().toString(36).slice(2, 9)}`;
    panel.setAttribute('aria-labelledby', titleId);
    const titleEl = document.createElement('p');
    titleEl.id = titleId;
    titleEl.className = 'text-xs font-semibold uppercase tracking-wide text-ink-muted mb-2';
    titleEl.textContent = title;
    panel.append(titleEl);
  }

  const body = document.createElement('div');
  body.setAttribute('data-role', 'picker-popover-body');
  panel.append(body);

  function closePopover() {
    if (currentClose !== closePopover) return;
    currentClose = null;

    document.removeEventListener('keydown', onKeydown);
    document.removeEventListener('pointerdown', onOutsidePointerDown, true);

    exitAndRemove(panel, 'anim-fade-out');

    if (trigger && typeof trigger.focus === 'function') {
      trigger.focus();
    }

    if (onClose) {
      try { onClose(); } catch {}
    }
  }

  body.append(resolveContent(content, { close: closePopover }));

  function onKeydown(e) {
    if (e.key === 'Escape') closePopover();
  }
  function onOutsidePointerDown(e) {
    const target = e.target;
    if (panel.contains(target)) return;
    if (trigger && (target === trigger || trigger.contains(target))) return;
    closePopover();
  }
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('pointerdown', onOutsidePointerDown, true);
  trapTab(panel);

  document.body.append(panel);

  if (trigger && typeof trigger.getBoundingClientRect === 'function') {
    const triggerRect = trigger.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth || 1024,
      height: window.innerHeight || 768,
    };
    const { top, left } = computePopoverPosition(triggerRect, viewport, {
      width: panelRect.width || 320,
      height: panelRect.height || 320,
    });
    panel.style.top = `${top}px`;
    panel.style.left = `${left}px`;
  }

  focusFirstFocusable(panel);

  currentClose = closePopover;
  return { close: closePopover };
}

/**
 * Opens a picker surface presenting one content payload in two responsive
 * modes: a drag-dismissable bottom sheet below `lg` (reusing bottom-sheet.js
 * mechanics/thresholds), or an anchored popover-dialog at `≥ lg`. See
 * design D6 / guided-forms spec "Picker surface has dual responsive
 * presentation".
 *
 * @param {object} opts
 * @param {HTMLElement} opts.trigger — element the popover anchors to; also
 *   receives focus back on close.
 * @param {string} [opts.title]
 * @param {HTMLElement|((api:{close():void}) => HTMLElement)} opts.content
 * @param {function} [opts.onClose]
 * @returns {{ close(): void }}
 */
export function pickerSheet({ trigger, title, content, onClose }) {
  const presentation = pickerPresentation(isDesktopViewport());
  if (presentation === 'popover') {
    return openAsPopover({ trigger, title, content, onClose });
  }
  return openAsBottomSheet({ trigger, title, content, onClose });
}
